// src/encounter/add-surface.tsx
//
// One dialog, two surfaces — chosen by WHERE it is rendered, not by a flag each caller sets.
//
// THE PROBLEM. Every rail add dialog (flags, medication, allergy, problem, procedure, vaccine,
// family history, social history, occupation, education) opens `<Modal open onClose title>`.
// Ahmed's instruction, 2026-09-07: every `+` in the first column must open an ANCHORED POPOVER,
// as the deployed provider app does — "then we can handle the style of the inner child".
//
// But each of those dialogs serves MORE THAN ONE entry point:
//
//   · the pre-encounter section `+`        → anchored to the `+`               → popover
//   · a pre-encounter row's Edit/Stop/Note → anchored to that row's own glyph   → popover
//   · the in-encounter BANNER              → a band with no anchor at all       → centred modal
//
// (The row actions came second, on Ahmed's repeat of the instruction the same day: they each have
// their own 12px glyph button, so the "no anchor" reading of them was wrong. The BANNER is the
// one surface that genuinely has nowhere to hang from.)
//
// So the surface cannot be a property of the dialog. It is a property of the CONTEXT the dialog
// is rendered in. `AddSurface` reads that context: rendered inside `RailAnchoredSurface` (via
// `AddSurfaceInPopover`) it emits the popover body; rendered anywhere else it emits the `Modal`
// exactly as before. Every dialog swaps `Modal` → `AddSurface` and changes nothing else, and the
// banner keeps the surface it already had.
//
// CORRECTED: an earlier version of this note claimed "no form layout is touched". That is no
// longer true and should not be relied on. Bringing the container to the deployed spec narrowed
// every surface to one 311px width (it used to mirror the DS `Modal`'s 364/480/640), so the
// multi-field forms DO reflow; the PA's `encounter-design-overrides.css` (deleted since PA-Z, 2026-09-23) grew ~337 lines of §21/§22 to
// carry the container chrome, the scroll mask, the sticky group label and the sticky footer; and
// all ten dialogs gained a `data-slot="add-surface-footer"` marker. What is still untouched is
// each form's own field ORDER and validation. Measured consequence to watch: at 311px the family
// history From/To date fields resolve to ~116px each, of which ~66px is text box, so a date reads
// clipped on the right (typeable, not lost).
//
// WHY A CONTEXT AND NOT A PROP. A prop would have to be threaded through ten dialogs and every
// call site, and each site would then be free to get it wrong — an "add" opening a modal, or an
// "edit" trying to anchor to a `+` that is not there. Reading the tree makes the wrong
// combination unrepresentable.
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Button, Modal, SummarySectionPopoverFooter } from '@scripsteam/scrips-signal-ds';
import { RailAddPopoverBody, type RailAddSurfaceSize } from './rail-add-popover';
import { AP_DIALOG_LABELS } from './ap-dialog-label-style';

export type AddSurfaceCloseRef = { current: (() => void) | null };

/** What the CONTAINER needs to know about the content rendered inside it.
 *
 *  The container is the Signal DS `SummarySectionPopover`, and it takes the title and the
 *  commit action as PROPS — but both are declared by the content (a dialog's `AddSurface
 *  title=…`, or a direct `RailAddPopoverBody`), which renders as that container's `children`.
 *  So they travel UP, through `useRailSurfaceHeader` below.
 *
 *  Every field here is compared BY VALUE. `hasCommit` is a boolean rather than the callback
 *  because a host passing `onCommit={() => …}` hands a new function identity on every render,
 *  and publishing that as state would re-render the surface, which re-runs `render({ close })`,
 *  which hands the dialog a new `onCommit`, which publishes again — "Maximum update depth
 *  exceeded". The callback itself is reached through `commitRef`, which is not state. */
export interface RailSurfaceHeader {
  /** The header band's label. */
  title?: string;
  /** Whether the content supplied a commit action — the header checkmark is disabled without
   *  one, exactly as the frozen anchor draws it. */
  hasCommit: boolean;
  /** Whether the scrolling body must RESERVE its height so the surface cannot resize under the
   *  user mid-interaction — the container maps it to the DS `bodyHeight="reserved"`. Decided by
   *  `RailAddPopoverBody`, which knows whether content can still arrive. */
  reserve: boolean;
  /** Whether the content has a type-to-filter field. The container then renders the DS
   *  `filter` band with an empty SLOT element in it, and the content PORTALS its field into that
   *  slot (`useRailFilterSlot`). Only the boolean travels up: publishing the field itself as
   *  state would re-render the container on every keystroke and re-run `render({ close })`. */
  filterable?: boolean;
  /** The header checkmark's accessible name ("Save allergy", "Stop medication"). Absent → the
   *  DS default ("Confirm"), which is what a picker with no commit keeps. */
  confirmLabel?: string;
  /** Surface width in px, through the DS molecule's own `width` prop. Absent → the DS default
   *  (the rail card's measured 311). */
  width?: number;
  /** The DS chrome (`SummarySectionPopover` `variant`). Absent → `rail`. */
  variant?: RailSurfaceVariant;
  /** Whether the checkmark is drawn at all (DS `showConfirm`). Absent → drawn. An explicit field,
   *  NOT derived from `hasCommit`: a surface with no commit YET (the Edit-medication loading body)
   *  keeps its grey checkmark, so the glyph does not pop in when the form mounts. */
  showConfirm?: boolean;
}

/** The two DS chromes a rail surface can take. */
export type RailSurfaceVariant = 'rail' | 'detail';

interface InPopoverValue {
  inPopover: boolean;
  /** Where the surface publishes its `onClose` so the popover can run it on dismissal.
   *  A REF, not an effect cleanup — see the note on `AddSurface`. */
  closeRef?: AddSurfaceCloseRef;
  /** Live handle on the CURRENT commit callback. A ref, so a new function identity per render
   *  costs nothing and cannot feed a render loop. */
  commitRef?: { current: (() => void) | undefined };
  /** Publish the header's VALUE to the container. Stable identity. */
  publishHeader?: (header: RailSurfaceHeader) => void;
  /** True when the container renders the DS `filter` band for a `filterable` header. Absent → a
   *  container that predates the band, and the content keeps its filter inline in the body. */
  hostsFilter?: boolean;
  /** The element inside the DS `filter` band the content's field portals into; `null` until the
   *  band has mounted. */
  filterSlot?: HTMLElement | null;
}

const InPopoverContext = createContext<InPopoverValue>({ inPopover: false });

/** Marks the subtree as living inside an anchored rail popover. Set by the rail's anchored
 *  surface; nothing else should provide it. */
export function AddSurfaceInPopover({
  children,
  closeRef,
  commitRef,
  publishHeader,
  hostsFilter,
  filterSlot,
}: {
  children: ReactNode;
  closeRef?: AddSurfaceCloseRef;
  commitRef?: { current: (() => void) | undefined };
  publishHeader?: (header: RailSurfaceHeader) => void;
  hostsFilter?: boolean;
  filterSlot?: HTMLElement | null;
}) {
  const value = useMemo<InPopoverValue>(
    () => ({ inPopover: true, closeRef, commitRef, publishHeader, hostsFilter, filterSlot }),
    [closeRef, commitRef, publishHeader, hostsFilter, filterSlot],
  );
  return <InPopoverContext.Provider value={value}>{children}</InPopoverContext.Provider>;
}

/** True when the surrounding surface is an anchored popover rather than a centred modal.
 *  Exposed so a dialog can drop a redundant footer "Close" the popover already provides by
 *  outside-press and Escape — not so it can re-decide the surface. */
export function useInAddPopover(): boolean {
  return useContext(InPopoverContext).inPopover;
}

/**
 * Publish this surface's header to the container that draws it.
 *
 * Called by `RailAddPopoverBody`, which is the only thing that renders a rail popover's content.
 * `useLayoutEffect` rather than `useEffect` so the header is filled in before paint — the
 * container's first commit necessarily has no title, because the title arrives from its own
 * children.
 *
 * The commit callback is written to a REF on every commit and never published as state. See
 * `RailSurfaceHeader` for the render loop that guards against.
 */
export function useRailSurfaceHeader(header: RailSurfaceHeader, onCommit?: () => void): void {
  const { commitRef, publishHeader } = useContext(InPopoverContext);
  // No dependency array: this must track the latest closure, and assigning a ref is idempotent
  // so StrictMode's double invoke is harmless.
  useLayoutEffect(() => {
    if (commitRef) commitRef.current = onCommit;
  });
  const { title, hasCommit, reserve, filterable = false, confirmLabel, width, variant, showConfirm } = header;
  useLayoutEffect(() => {
    publishHeader?.({ title, hasCommit, reserve, filterable, confirmLabel, width, variant, showConfirm });
  }, [publishHeader, title, hasCommit, reserve, filterable, confirmLabel, width, variant, showConfirm]);
}

/** Where a rail surface's type-to-filter field goes: `hostsFilter` false → inline in the body (a
 *  container without the DS band); true → portal into `filterSlot` once it exists. */
export function useRailFilterSlot(): { hostsFilter: boolean; filterSlot: HTMLElement | null } {
  const { hostsFilter = false, filterSlot = null } = useContext(InPopoverContext);
  return { hostsFilter, filterSlot };
}

/** A dialog's COMMIT action, declared once and drawn by whichever surface the dialog lands in. */
export interface AddSurfaceCommit {
  /** The dialog's own submit handler. Its inline validation (`setError`) is unchanged. */
  run: () => void;
  /** The specific name of the action — "Save allergy", "Stop medication". It is the header
   *  checkmark's accessible name on the anchored surface (DS `confirmLabel`). */
  label: string;
  /** Visible text of the commit BUTTON on the centred-modal surface ("Add", "Saving…"). Absent →
   *  `label`. The modal keeps the wording it always had. */
  text?: string;
  /** The action cannot run yet (Medications: no drug picked). */
  disabled?: boolean;
  /** A save is in flight: the commit cannot run again and the modal's Cancel is held too. */
  busy?: boolean;
  /** Modal-surface button variant. The DS header checkmark has no tone of its own. */
  tone?: 'primary' | 'destructive';
}

export interface AddSurfaceProps {
  /** Same contract as the DS `Modal`: `false` renders nothing. In popover context the popover
   *  already governs mounting, but an explicit `false` is still honoured. */
  open?: boolean;
  onClose: () => void;
  /** `Modal.title` is a plain string in the DS contract, and every rail dialog passes one. */
  title?: string;
  /** Passed straight to `Modal` when centred; maps to the popover's width when anchored. */
  size?: RailAddSurfaceSize;
  /**
   * The dialog's commit. ANCHORED: the header checkmark runs it (enabled unless `disabled` or
   * `busy`) and the dialog draws no footer — Ahmed's ruling, 2026-10-07: the header X cancels, the
   * checkmark saves, no Cancel/Save row. CENTRED (the in-encounter banner, which has no header
   * checkmark): this component draws the Cancel + commit row itself, so the banner keeps working.
   * Either way Ctrl/Cmd+Enter inside the form runs it; plain Enter does not, because Enter in a
   * typeahead picks a result.
   */
  commit?: AddSurfaceCommit;
  /** Anchored surface width in px (DS `width`). Absent → the DS default. */
  popoverWidth?: number;
  /** Anchored surface chrome (DS `variant`). Absent → `rail`. `detail` is the Flutter production
   *  add/detail popup and owns its width (336), so it needs no `popoverWidth`. */
  popoverVariant?: RailSurfaceVariant;
  /** Anchored surface only: whether the body RESERVES its height (DS `bodyHeight="reserved"`).
   *  Absent → `RailAddPopoverBody` decides from the form's DOM (reserved only while a search shows
   *  results or "Searching…"; otherwise the form hugs). A form
   *  that knows when content can still arrive under the caret says so here. */
  reserve?: boolean;
  children?: ReactNode;
}

/** Where a failed commit's inline error is: the dialogs render it with the DS `HelperText
 *  tone="error"`, which carries `role="alert"`. */
const INLINE_ERROR = '[role="alert"]';

/**
 * The add/edit surface. Drop-in for `<Modal open onClose title size>`.
 */
export function AddSurface({
  open = true,
  onClose,
  title,
  size,
  commit,
  popoverWidth,
  popoverVariant,
  reserve,
  children,
}: AddSurfaceProps) {
  const ctx = useContext(InPopoverContext);
  const inPopover = ctx.inPopover;
  const bodyRef = useRef<HTMLDivElement>(null);

  // PUBLISH `onClose` for the popover to run on dismissal. Deliberately a ref assignment and
  // NOT an effect cleanup — that distinction is the whole point of this block.
  //
  // What it is for: a popover is dismissed by @base-ui itself (Escape, outside press), which
  // only flips the popover's own `open`. Without this, the dialog unmounted and its `onClose`
  // never ran, so its own fields reset but every HOST-owned field survived — `allergyQuery`,
  // `conditionQuery`, `procedureQuery`, `vaxQuery`, `relationshipQuery` + `familyDxQuery`,
  // `socialQuery`, `medQuery` + `medForDi`. Press Escape, press `+` again, and a blank form sat
  // over the previous search's results. `medForDi` was worse: it feeds the drug-interaction
  // query two other dialog instances read.
  //
  // WHY NOT AN UNMOUNT CLEANUP — measured, and it shipped this bug to Ahmed. The first version
  // ran `onClose` from an effect's cleanup. React **StrictMode** (on in `src/main.tsx`)
  // double-invokes effects in development: mount → cleanup → mount. So the cleanup fired the
  // instant the surface opened, calling the host's `onClose`, which calls the popover's
  // `close()`. Every dialog-backed `+` opened and shut in the same frame — `aria-expanded` was
  // already `false` 60ms after the click. It read as a dead button. Patient Flags was the only
  // one that worked, and only because it renders `RailAddPopoverBody` directly and never
  // mounts this component.
  //
  // A ref assignment is idempotent, so a double-invoke is harmless, and the popover owns the
  // decision of when a dismissal has actually happened.
  const latestClose = useRef(onClose);
  latestClose.current = onClose;
  useEffect(() => {
    const ref = ctx.closeRef;
    if (!inPopover || !ref) return;
    ref.current = () => latestClose.current();
    // No cleanup: the popover clears its own ref when it dismisses, and clearing it here would
    // reintroduce the StrictMode race above.
  }, [inPopover, ctx.closeRef]);

  if (!open) return null;

  const armed = !!commit && !commit.disabled && !commit.busy;
  // Run the commit, then bring a validation error into view. The checkmark is now at the TOP of
  // the surface and the dialogs render their error at the BOTTOM of the form, below the 320px
  // fold on the longer forms — a press that only set an off-screen error would read as a dead
  // button. Next frame, so the error the handler just set has rendered. `scrollIntoView` is
  // optional-called: jsdom does not implement it.
  const runCommit = () => {
    if (!armed || !commit) return;
    commit.run();
    requestAnimationFrame(() => {
      bodyRef.current?.querySelector<HTMLElement>(INLINE_ERROR)?.scrollIntoView?.({ block: 'nearest' });
    });
  };
  // Ctrl/Cmd+Enter commits from anywhere in the form. NOT plain Enter: in a typeahead Enter picks
  // the highlighted result, and a save on that key would submit a half-filled form.
  const commitOnModEnter = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' || !(e.ctrlKey || e.metaKey) || !armed) return;
    e.preventDefault();
    runCommit();
  };

  // ONE FIELD-LABEL TREATMENT, on both surfaces.
  //
  // Measured in the live Allergies popover: FOUR label idioms stacked vertically in one
  // 311px form — `ALLERGEN` (11px uppercase hint), `Severity` / `Reaction` (the DS's own
  // 13px medium sentence case), `START DATE` (uppercase, plus an ⓘ) and `Description`. The
  // eye reads that as four kinds of thing rather than four fields.
  //
  // The target is NOT a new choice: PROD-1801 already ruled it for the A&P order dialogs
  // (Andrew, 2 Aug — "the numeric picker title is upper case and the normal title is
  // pascal, I want all to be the same"), and `ap-dialog-label-style.tsx` is that ruling,
  // including the two traps it records — the DS has THREE label shapes, so `label > span`
  // is required or `Select`/`DatePicker` are silently missed, and selection-control labels
  // must be excluded or the patient's own conditions get SHOUTED. Reusing it means these
  // forms join a settled treatment instead of inventing a fifth idiom.
  //
  // WRAPPED HERE, not inside `RailAddPopoverBody`: the same dialog renders as a centred
  // `Modal` from the in-encounter banner, and normalising only the anchored surface would
  // leave one dialog with two different label treatments depending on where it opened.
  //
  // BOTH surfaces draw the same commit row now (Antonin, 16 Oct: "left panel when I press + is opening a small modal, clean it
  // up, more spacing, cleaner buttons (compliant)"): Cancel (secondary) and the one primary action (brand blue), right-aligned, the
  // control height, sentence case. It replaces the anchored surface's 16 px header glyphs (an icon-only check as the primary
  // action), which reversed Ahmed's 7 Oct ruling of "the header X cancels, the checkmark saves": the header keeps its X; the
  // checkmark is not drawn when the dialog has a commit (`showConfirm={false}` below). Same handler, same armed state.
  const body = (
    <div ref={bodyRef} className={AP_DIALOG_LABELS} onKeyDown={commitOnModEnter}>
      {children}
      {commit && (
        <SummarySectionPopoverFooter className="mt-4">
          <Button variant="secondary" size="md" onClick={onClose} disabled={commit.busy}>
            Cancel
          </Button>
          <Button variant={commit.tone ?? 'primary'} size="md" onClick={runCommit} disabled={!armed}>
            {/* The anchored surface's visible wording IS the action's name ("Save allergy", as its header checkmark was named), so the
                accessible name and the visible text match (WCAG 2.5.3); while a save runs it reads the dialog's own "Saving…". The
                centred modal keeps the dialog's short wording ("Add"). */}
            {inPopover && !commit.busy ? commit.label : (commit.text ?? commit.label)}
          </Button>
        </SummarySectionPopoverFooter>
      )}
    </div>
  );
  if (inPopover) {
    // No close button and no title duplication beyond the popover's own header: dismissal is
    // @base-ui's (Escape, outside press), and `SectionAddPopover` already names the surface for
    // assistive tech from the trigger's label. The header's X cancels; its checkmark commits, armed
    // only while the commit can run.
    return (
      <RailAddPopoverBody
        title={title}
        size={size}
        onCommit={armed ? runCommit : undefined}
        confirmLabel={commit?.label}
        showConfirm={commit ? false : undefined}
        width={popoverWidth}
        variant={popoverVariant}
        reserve={reserve}
      >
        {body}
      </RailAddPopoverBody>
    );
  }
  return (
    <Modal open onClose={onClose} title={title} size={size}>
      {body}
    </Modal>
  );
}
